/**
 * Gateway API Client — the session probe, and nothing else.
 *
 * AXF-1013: this file used to hold `login`, `logout` and `extractSessionIdFromUrl` as well. The
 * portal has no sign-in and no sign-out of its own any more, so all three are gone along with the
 * page that called each of them. What is left is one read of the shared session cookie.
 */
import { ENV } from '@shared/config/env'
import { createLoggerWithPrefix } from '@shared/utils/logger'
import { ENDPOINTS } from './endpoints'

const logger = createLoggerWithPrefix('gatewayClient')

/**
 * The result of the session probe. Three cases, not two: the portal must render the resting page
 * for a signed-out user AND for a failed call, but only the second is a fault worth logging as one.
 */
export type SessionProbe =
  | { status: 'authenticated'; raw: unknown }
  | { status: 'unauthenticated' }
  | { status: 'error'; error: Error }

export const gatewayClient = {
  /**
   * The session probe — the portal's ONLY way of knowing who it is talking to.
   *
   * AXF-1013: the portal has no sign-in of its own. The single source of truth is the
   * `axflow_session` cookie the customer app's sign-in already set on the API host, which the
   * browser attaches here because the portal, the API and AX WorkFlow are all same-site under
   * `auxalia.app` (spec C1). `credentials: 'include'` is what carries it; there is deliberately no
   * `X-Session-Id` fallback and no localStorage read, because nothing writes either any more and a
   * bootstrap that preferred them would work for the team and fail for everyone else (spec R5).
   *
   * The endpoint is `/api/usm/v1/users/me` (`CurrentUserController`), NOT `/auth/me`. They return
   * different shapes and `gatewayMapper` accepts only this one — see the spec's revision-4 note.
   *
   * Three outcomes, because the caller must tell "no session" from "could not ask":
   *   - `authenticated` — 200, body handed on for `mapGatewayResponseToUser` to validate
   *   - `unauthenticated` — 401, the resting page
   *   - `error` — anything else, also the resting page, but logged as a fault rather than a
   *     signed-out user
   */
  getCurrentUser: async (): Promise<SessionProbe> => {
    const gatewayUrl = ENV.GATEWAY_URL
    if (!gatewayUrl) {
      const error = new Error('Gateway URL not configured. Set VITE_GATEWAY_URL.')
      logger.error(error.message)
      return { status: 'error', error }
    }

    try {
      const response = await fetch(`${gatewayUrl}${ENDPOINTS.GATEWAY.ME}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      })

      if (response.status === 401) {
        logger.info('No session — the portal renders the resting page.')
        return { status: 'unauthenticated' }
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => '')
        const error = new Error(`${ENDPOINTS.GATEWAY.ME} failed: ${response.status} - ${errorText}`)
        logger.error(error.message)
        return { status: 'error', error }
      }

      return { status: 'authenticated', raw: await response.json() }
    } catch (cause) {
      const error = cause instanceof Error ? cause : new Error(String(cause))
      logger.error('Session probe failed:', error)
      return { status: 'error', error }
    }
  }
}
